import { useEffect, useMemo, useState } from 'react';
import { apiUrl } from '../api.js';
import { cardArt } from '../cardArt.js';
import Pista from './Pista.jsx';

/**
 * EL NUCLEO DE UN MAZO EN SPRITES, componente compartido.
 *
 * Nacio dentro de MazosPublicosView para la tabla de la comunidad y en
 * ago-2026 lo reclamo tambien la tabla de arquetipos del meta: es EL patron
 * de la casa para "esta fila es un mazo" -- toda tabla que liste mazos o
 * arquetipos pinta su nucleo debajo del nombre. Vive aparte para que el
 * proximo consumidor (matchups de Partidas, standings de torneos) no copie
 * el fallback en cadena ni el resumen accesible.
 *
 * La forma del dato es la que sirven /api/mazos y /api/meta/archetypes:
 * [{ cardId, name, count, sprites: string[], image, imageFallback }].
 */

/**
 * La miniatura de un Pokemon del nucleo.
 *
 * ES UN SPRITE, no el arte de la carta recortado. Se probo con el recorte y a
 * 30 px no se reconoce nada: la ilustracion de una carta es un cuadro con
 * fondo, y reducida queda una mancha de color. El sprite es la silueta sola
 * sobre nada -- por eso Limitless identifica asi sus arquetipos y por eso su
 * lista se lee de un vistazo.
 *
 * CADENA DE CANDIDATOS, igual que SetLogo con los logos de expansion: las
 * formas no se nombran en el sprite como en la carta ("Cornerstone Mask
 * Ogerpon" es `ogerpon-cornerstone`), asi que el servidor manda varias
 * posibilidades y aqui se cae a la siguiente en cada fallo. El ultimo respaldo
 * es el arte de la carta: peor miniatura, pero mejor que un hueco.
 */
export function Mini({ carta }) {
  const candidatos = useMemo(
    () => [
      ...(carta.sprites ?? []).map((u) => apiUrl(`/api/img?u=${encodeURIComponent(u)}`)),
      cardArt(carta),
    ].filter(Boolean),
    [carta]
  );
  const [i, setI] = useState(0);
  /* Al cambiar de pagina la misma posicion recibe otra carta: sin reiniciar,
     heredaba el indice de fallo de la anterior y saltaba candidatos buenos. */
  useEffect(() => setI(0), [candidatos]);

  const src = candidatos[i];
  if (!src) return null;
  const esSprite = i < (carta.sprites?.length ?? 0);

  return (
    <span className={`nucleo__mini${esSprite ? ' is-sprite' : ''}`}>
      <img
        src={src}
        alt=""
        /* El alt va vacio a proposito: el nombre ya viaja en la pista y en el
           resumen de texto de la fila, y repetirlo por carta convertiria cada
           fila en cinco anuncios seguidos. */
        aria-hidden="true"
        loading="lazy"
        decoding="async"
        onError={() => setI((n) => n + 1)}
      />
      {carta.count > 1 && <span className="nucleo__minin" aria-hidden="true">{carta.count}</span>}
    </span>
  );
}

export default function Nucleo({ cartas, t, sinCuenta = false }) {
  if (!cartas?.length) return null;
  return (
    <span className="nucleo">
      {cartas.map((c) => (
        <Pista key={`${c.cardId}-${c.name}`} texto={sinCuenta ? c.name : `${c.count}× ${c.name}`}>
          <Mini carta={sinCuenta ? { ...c, count: 1 } : c} />
        </Pista>
      ))}
      {/* El resumen en TEXTO existe para quien no ve las miniaturas: es la
          misma linea, dicha con palabras. */}
      <span className="sr-only">
        {t('publicCore')}: {cartas.map((c) => (sinCuenta ? c.name : `${c.count}× ${c.name}`)).join(', ')}
      </span>
    </span>
  );
}
